import React, { useState, useRef } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { Loader, Send, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
export default function ContactFormContactFormSection() {
  return <section className="relative overflow-hidden bg-[#62751D]/10 py-16 md:py-20" id="contact">
      <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-[600px] h-[600px] bg-[#62751D]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-[#62751D]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="animate-in fade-in slide-in-from-bottom-8 duration-1000">
            <div className="bg-white/90 backdrop-blur-xl rounded-sm shadow-2xl p-8 md:p-12 border border-[#62751D]/10 relative">
              <div className="absolute top-0 left-0 w-1 h-24 bg-[#62751D]" />
              <FormProvider formId="contact_form_contact" sectionName="contact_form_contact_form_section" defaultValues={{
              name: "",
              organization: "",
              email: "",
              phone: "",
              subject: "",
              message: "",
              privacy_consent: false
            }} validationRules={{
              name: {
                required: true
              },
              organization: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              subject: {
                required: true
              },
              message: {
                required: true
              },
              privacy_consent: {
                required: true
              }
            }} submitText="Envoyer ma demande" successMessage="Votre message a bien été envoyé. Je reviendrai vers vous dans les plus brefs délais." errorMessage="Une erreur est survenue lors de l'envoi. Veuillez réessayer ou me contacter directement.">
                <ContactFormContent />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting: wvcSubmitting,
    isSubmitted: wvcSubmitted,
    submitError: wvcError,
    successMessage,
    handleSubmit: wvcHandleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [honeypot, setHoneypot] = useState("");
  const formLoadTime = useRef(Date.now());
  const MIN_SUBMIT_DELAY = 3000;
  const [spamError, setSpamError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const WEB3FORMS_KEY = "c843f390-0d72-4a23-9079-daf657cacb36";
  const handleSubmitWithEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setSpamError("");
    setSubmitError("");

    // Protection 1 : honeypot custom
    if (honeypot) {
      setSpamError("Une erreur est survenue. Veuillez réessayer.");
      return;
    }

    // Protection 2 : délai minimum
    if (Date.now() - formLoadTime.current < MIN_SUBMIT_DELAY) {
      setSpamError("Veuillez patienter quelques secondes avant d'envoyer le formulaire.");
      return;
    }
    const formElement = e.target as HTMLFormElement;
    const formData = new FormData(formElement);

    // Protection 3 : vérification côté client que les champs essentiels ne sont pas vides
    const name = formData.get("name")?.toString().trim() || "";
    const email = formData.get("email")?.toString().trim() || "";
    const message = formData.get("message")?.toString().trim() || "";
    if (!name || !email || !message) {
      setSubmitError("Merci de remplir au minimum votre nom, votre email et votre message.");
      return;
    }
    formData.append("access_key", WEB3FORMS_KEY);
    formData.append("subject", "Nouveau message depuis effet-conseil.com");
    formData.append("from_name", "Formulaire Effet Conseil");
    setIsSubmitting(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });
      const result = await response.json();
      if (result.success) {
        setIsSubmitted(true);
        try {
          await wvcHandleSubmit(e);
        } catch {
          // Pas bloquant si wvcClient échoue
        }
      } else {
        setSubmitError("Une erreur est survenue lors de l'envoi. Veuillez réessayer ou me contacter directement.");
      }
    } catch (error) {
      setSubmitError("Une erreur est survenue lors de l'envoi. Veuillez réessayer ou me contacter directement.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleReset = () => {
    setIsSubmitted(false);
    setSubmitError("");
    setSpamError("");
    formLoadTime.current = Date.now();
    resetForm();
  };
  if (isSubmitted) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in zoom-in-95 duration-500">
        <div className="size-20 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle2 className="size-10 text-green-600" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-slate-900">
          Message envoyé !
        </h3>
        <p className="text-slate-600 max-w-sm mx-auto">{successMessage}</p>
        <Button onClick={handleReset} variant="outline" className="rounded-sm border-primary text-primary hover:bg-primary/5">
          Envoyer un autre message
        </Button>
      </div>;
  }
  return <form onSubmit={handleSubmitWithEmail} className="space-y-8">
      {/* Honeypot custom (invisible pour les humains) */}
      <div className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10 overflow-hidden" aria-hidden="true">
        <label htmlFor="website_url">Ne pas remplir ce champ</label>
        <input type="text" id="website_url" name="website_url" tabIndex={-1} autoComplete="off" value={honeypot} onChange={e => setHoneypot(e.target.value)} />
      </div>

      {/* Honeypot natif Web3Forms (rejeté côté serveur si coché) */}
      <input type="checkbox" name="botcheck" className="hidden" style={{
      display: "none"
    }} tabIndex={-1} autoComplete="off" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-bold text-slate-800">
                Prénom et nom <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input required minLength={2} className="rounded-sm border-slate-200 focus:border-primary h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-bold text-slate-800">
                Organisme / Structure <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input required className="rounded-sm border-slate-200 focus:border-primary h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-bold text-slate-800">
                Email <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" required className="rounded-sm border-slate-200 focus:border-primary h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-serif font-bold text-slate-800">
                Téléphone (Optionnel)
              </FormLabel>
              <FormControl>
                <Input type="tel" className="rounded-sm border-slate-200 focus:border-primary h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-serif font-bold text-slate-800">
              Votre besoin <span className="text-red-500">*</span>
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="w-full rounded-sm border-slate-200 focus:border-primary h-12">
                  <SelectValue placeholder="Sélectionnez l'objet de votre demande" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Certification professionnelle RNCP/RS">
                  Certification RNCP ou RS
                </SelectItem>
                <SelectItem value="Certification Qualiopi®">
                  Certification Qualiopi®
                </SelectItem>
                <SelectItem value="Cadre juridique et conformité">
                  Cadre juridique
                </SelectItem>
                <SelectItem value="Autre besoin / Question générale">
                  Autre besoin / Question générale
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-serif font-bold text-slate-800">
              Votre message <span className="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Textarea required minLength={10} placeholder="Décrivez brièvement votre projet, vos enjeux ou vos questions. Je vous recontacterai dans les plus brefs délais." className="rounded-sm border-slate-200 focus:border-primary min-h-[150px] resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="privacy_consent" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 p-4 bg-slate-50 rounded-sm border border-slate-100">
            <FormControl>
              <input type="checkbox" required className="size-5 rounded-sm border-slate-300 text-primary" checked={field.value} onChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-tight">
              <FormLabel className="text-xs text-slate-600 cursor-pointer font-normal leading-snug">
                J'accepte que mes données soient utilisées par Effet Conseil
                dans le cadre de ma demande de contact.{" "}
                <Link to="/PrivacyPolicyPage" className="text-primary hover:underline font-medium">
                  Politique de confidentialité
                </Link>
                {" "}<span className="text-red-500">*</span>
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {spamError && <div className="p-4 bg-red-50 border border-red-100 rounded-sm flex items-center gap-3 text-red-800">
          <p className="text-sm">{spamError}</p>
        </div>}
      {submitError && <div className="p-4 bg-red-50 border border-red-100 rounded-sm flex items-center gap-3 text-red-800">
          <p className="text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-serif font-bold rounded-sm bg-primary text-white hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 group">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Envoi en cours...
          </> : <>
            Envoyer ma demande
            <Send className="size-5 ml-2 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </form>;
}